/**
 * The commands the person allowed for the rest of a session on this computer,
 * kept in `sessionStorage` per session, as the event tail is
 * (`stored-tail.ts`), so a reload of the page keeps them. Another window has
 * storage of its own and asks again. What is read back is checked: an entry
 * that is not a list of exact shell commands keeps no grant and is dropped.
 */
import type { ExactCommand, GrantStore } from '@/lib/code-sessions/local-agent/consent';
import { z } from 'zod';

export const GRANTS_STORAGE_PREFIX = 'cubic:local-agent:grants:';

/**
 * An exact shell command, as `ExactCommand` has it: the whole command, its
 * folder, whether it runs in the background, and the time limit the card
 * showed for one that does. Typed as `ExactCommand`, so a field added there
 * fails to compile here until the check reads it too.
 */
const grantSchema: z.ZodType<ExactCommand> = z.object({
  command: z.string().min(1),
  workingFolder: z.string(),
  background: z.boolean(),
  timeoutMs: z.number().positive().optional(),
});

const storedGrantsSchema = z.object({ grants: z.array(grantSchema) });

/** The grants of each session kept in `storage`; none without one. */
export function storedGrants(storage: Storage | null): GrantStore | undefined {
  if (storage === null) {
    return undefined;
  }
  const keyOf = (sessionId: string) => `${GRANTS_STORAGE_PREFIX}${sessionId}`;
  return {
    read: (sessionId): ExactCommand[] => {
      const key = keyOf(sessionId);
      const value = storage.getItem(key);
      if (value === null) {
        return [];
      }
      try {
        return storedGrantsSchema.parse(JSON.parse(value)).grants;
      } catch {
        storage.removeItem(key);
        return [];
      }
    },
    write: (sessionId, grants) => {
      try {
        storage.setItem(keyOf(sessionId), JSON.stringify({ grants }));
      } catch {
        // Out of room: the grants last as long as this page, and a reload asks again.
      }
    },
    remove: (sessionId) => {
      storage.removeItem(keyOf(sessionId));
    },
  };
}
